/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {
  DynamicTreeView,
  TreeViewData
} from '@ion/dls/src/components/treeviews/DynamicTreeView';
import {
  TreeViewDataItem
} from '@ion/dls/src/components/treeviews/TreeViews';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import DynamicLookupHierarchyTooltipOption from './DynamicLookupHierarchyTooltipOption';
import DynamicLookupOptionsLoader from './DynamicLookupOptionsLoader';
import {currentThemeSuggestionItemStyle} from './suggestionItemStyles';
import {AccountIdLinkMap, DynamicDataItem, DynamicListData, Mapping} from './types';
import {filterDynamicDataList, isTreeView} from './utils';

const useStyles = makeStyles((theme: DLSTheme) => ({
  spinnerWrapper : {
    display : 'flex'
  },
  noResults : {
    minHeight  : 24,
    display    : 'flex',
    alignItems : 'center',
    padding    : '8px 32px 0px',
    color      : dlsColor('grey3'),
    ...dlsTypography('body3')
  },
  // current theme matches Dropdown.tsx's own `option` class exactly (same
  // padding/typography/color and hover highlight as SearchSuggestions.tsx's
  // own suggestionItem) - legacy keeps its own border-bottom-divided rows ...
  suggestionItem : toggleThemeStyle({
    height               : 40,
    padding              : '0 32px !important',
    fontSize             : 14,
    fontFamily           : theme.typography.fontFamily,
    '&:not(:last-child)' : {
      background : `linear-gradient(${theme.dls.colors.grey4}, ${theme.dls.colors.grey4}) bottom/ 95% 1px no-repeat`
    },
    '&:hover, &:focus' : {
      background : theme.dls.colors.grey5
    }
  }, currentThemeSuggestionItemStyle()),
  filesLookUpWrapper : {
    padding : 10
  }
}));

export interface DynamicLookUpProps {
  lookUp: DynamicListData;
  onSelection: Function;
  dropdownSearch: string;
  isLast: boolean;
  updateFilterLookUpData?: Function;
  parentClick?: boolean;
  refreshLast: Function;
  attributeMappings: {[key: string]: Mapping};
  isCaseInsensitive?: boolean;
  accountTree: AccountIdLinkMap;
}

export interface LoaderAndNoDataProps {
  isLoading: boolean;
  hasNoData: boolean;
  isDeviceFiles: boolean;
  classes: any;
}

const LoaderAndNoData = ({
  isLoading,
  hasNoData,
  isDeviceFiles,
  classes
}: LoaderAndNoDataProps) => {
  if (isLoading || hasNoData) {
    return (
      <div className={classes}>
        {isLoading && <DynamicLookupOptionsLoader />}
        {(!isLoading && hasNoData) && (
          <span>
            {translate(`common:modularsearch.${isDeviceFiles ? 'filterPermissions' : 'noresults'}`)}
          </span>
        )}
      </div>
    );
  }
  return null;
};

interface TreeViewImplProps {
  treeViewData: TreeViewData[] | any[];
  parentClick?: boolean
  onTreeViewLabelSelect?: (item: TreeViewDataItem) => void;
}

function isTreeViewDataPropsEqual(previousTreeViewProps: TreeViewImplProps, nextTreeViewProps: TreeViewImplProps) {
  return previousTreeViewProps?.treeViewData?.length === nextTreeViewProps?.treeViewData?.length;
};

const TreeViewImpl = React.memo(({parentClick, treeViewData, onTreeViewLabelSelect}: TreeViewImplProps) => {
  return (
    <DynamicTreeView data={treeViewData} parentClick={parentClick} onItemClick={onTreeViewLabelSelect}/>
  );
}, isTreeViewDataPropsEqual);

/**
 * Renders JSX component that will dynamically fetch data from API and allow single select.
 *
 * ```typescript
 * import DynamicLookUp from '@ion/features/src/search/DynamicLookUp';
 *
 *  <DynamicLookUp lookUp={lookUp}
 *    onSelection={onSelection}
 *    dropdownSearch={query[2]?.trim()}
 *    isLast={isLast}
 *    updateFilterLookUpData={updateFilterLookUpData}
 *    refreshLast={refreshLast}/>
 * ```
 */
const DynamicLookUp = ({
  lookUp,
  onSelection,
  dropdownSearch,
  isLast,
  updateFilterLookUpData,
  refreshLast,
  attributeMappings,
  parentClick = true,
  isCaseInsensitive = false,
  accountTree
}: DynamicLookUpProps) => {
  const classes                                 = useStyles();
  const [loading, isLoading]                    = useState<boolean>(false);
  const [lookupData, updateLookupData]          = useState<DynamicDataItem[]>([]);
  const [page, setPage]                         = useState<number>(1);
  const [filteredTreeData, setFilteredTreeData] = useState<DynamicDataItem[]>(null);
  const [isDeviceFiles, setIsDeviceFiles]       = useState<boolean>(false);

  const initialization = useCallback(async function() {
    const {data, isDeviceFile = false} = await lookUp?.loader(dropdownSearch) || [];
    const sorted = data?.sort?.() || [];
    updateFilterLookUpData?.(sorted);
    updateLookupData(sorted);
    isLoading(false);
    setIsDeviceFiles(isDeviceFile);
  }, [dropdownSearch]);

  useEffect(function() {
    isLoading(true);
    initialization();
  }, []);

  useEffect(function() {
    setPage(1);
    if (isTreeView(attributeMappings) && dropdownSearch?.length) {
      setFilteredTreeData(
        filterDynamicDataList(lookupData, dropdownSearch, -1, 'searchableString')
      );
    }
    if (!dropdownSearch?.length) {
      setFilteredTreeData(null);
    }
  }, [dropdownSearch]);

  useEffect(() => {
    if (isLast) {
      const numberOfPages = Math.ceil(lookupData.length/50);
      if (page < numberOfPages) setPage(page + 1);
      refreshLast(false);
    }
  }, [isLast]);

  const onTreeViewLabelSelect = useCallback(function(item: TreeViewData) {
    onSelection({...item, isTreeView: true})();
  }, []);

  const isTreeViewImplementation = useMemo(() => {
    return isTreeView(attributeMappings);
  }, [attributeMappings]);

  const filteredResults = useMemo(() => {
    if (!isTreeViewImplementation) {
      return filterDynamicDataList(
        lookupData,
        dropdownSearch,
        page * 50,
        'name',
        isCaseInsensitive);
    }
    return [];
  }, [
    isTreeViewImplementation,
    isCaseInsensitive,
    dropdownSearch,
    lookupData,
    page
  ]);

  return (
    <>
      <LoaderAndNoData classes={classes.spinnerWrapper}
        isLoading={loading}
        isDeviceFiles={isDeviceFiles}
        hasNoData={lookupData.length === 0}/>
      {(!loading && lookupData.length > 0) && (
        <>
          <ConditionalRender condition={!isTreeViewImplementation}>
            <ConditionalRender condition={filteredResults?.length}>
              {filteredResults.map((item: DynamicDataItem, index: number) => (
                <MenuItem id={`${item.id}-${index}`}
                  classes={{root: classes.suggestionItem}}
                  onClick={onSelection(item)}
                  key={`suggestion-attribute-${item.id}-${index}`}>
                  <DynamicLookupHierarchyTooltipOption accountTree={accountTree} item={item} />
                </MenuItem>
              ))}
            </ConditionalRender>
            <ConditionalRender condition={filteredResults?.length === 0}>
              <div className={classes.noResults}>
                {translate('common:modularsearch.noMatchingResults')}
              </div>
            </ConditionalRender>
          </ConditionalRender>
          <ConditionalRender condition={isTreeViewImplementation}>
            <TreeViewImpl parentClick={parentClick}
              treeViewData={filteredTreeData ?? lookupData}
              onTreeViewLabelSelect={onTreeViewLabelSelect}/>
          </ConditionalRender>
        </>
      )}
    </>
  );
};

export default DynamicLookUp;
